import React, {
  useEffect,
  useState,
} from "react";

import {
  FaLock,
  FaKey,
  FaShieldAlt,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaSpinner,
  FaCheckCircle,
  FaTimes,
  FaArrowLeft,
  FaSignOutAlt,
} from "react-icons/fa";

import {
  collection,
  getDocs,
  query,
  where,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import {
  useNavigate,
} from "react-router-dom";

import {
  db,
} from "../firebase";

import "./ChangePassword.css";


/* =========================================================
   SECURITY QUESTIONS
========================================================= */

const securityQuestions = [
  "What is your age?",
  "What is your lucky number?",
  "What was the name of your first school?",
  "What is your favorite childhood nickname?",
  "What was the name of your first pet?",
  "What is your favorite childhood memory?",
  "What is your mother's maiden name?",
  "What city were you born in?",
];


/* =========================================================
   PASSWORD TYPES
========================================================= */

const PASSWORD_TYPES = {
  LOGIN: "login",
  WITHDRAW: "withdraw",
};


/* =========================================================
   COMPONENT
========================================================= */

export default function ProfileChange() {

  const navigate = useNavigate();


  /* =======================================================
     USERNAME FROM LOCAL STORAGE
  ======================================================= */

  const username = String(
    localStorage.getItem("username") || ""
  ).trim();


  /* =======================================================
     USER DATA
  ======================================================= */

  const [
    userData,
    setUserData,
  ] = useState(null);


  /* =======================================================
     USER DOCUMENT REFERENCE
  ======================================================= */

  const [
    userDocRef,
    setUserDocRef,
  ] = useState(null);


  /* =======================================================
     SECURITY DATA
  ======================================================= */

  const [
    securityData,
    setSecurityData,
  ] = useState(null);


  /* =======================================================
     SECURITY DOCUMENT REFERENCE
  ======================================================= */

  const [
    securityDocRef,
    setSecurityDocRef,
  ] = useState(null);


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(true);


  /* =======================================================
     PASSWORD TYPE
  ======================================================= */

  const [
    passwordType,
    setPasswordType,
  ] = useState(null);


  /* =======================================================
     SECURITY QUESTION
  ======================================================= */

  const [
    selectedQuestion,
    setSelectedQuestion,
  ] = useState("");


  /* =======================================================
     SECURITY ANSWER
  ======================================================= */

  const [
    securityAnswer,
    setSecurityAnswer,
  ] = useState("");


  /* =======================================================
     OLD PASSWORD
  ======================================================= */

  const [
    oldPassword,
    setOldPassword,
  ] = useState("");


  /* =======================================================
     NEW PASSWORD
  ======================================================= */

  const [
    newPassword,
    setNewPassword,
  ] = useState("");


  /* =======================================================
     CONFIRM PASSWORD
  ======================================================= */

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState("");


  /* =======================================================
     SHOW OLD PASSWORD
  ======================================================= */

  const [
    showOldPassword,
    setShowOldPassword,
  ] = useState(false);


  /* =======================================================
     SHOW NEW PASSWORD
  ======================================================= */

  const [
    showNewPassword,
    setShowNewPassword,
  ] = useState(false);


  /* =======================================================
     SHOW CONFIRM PASSWORD
  ======================================================= */

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);


  /* =======================================================
     SAVING
  ======================================================= */

  const [
    saving,
    setSaving,
  ] = useState(false);


  /* =======================================================
     MESSAGE
  ======================================================= */

  const [
    message,
    setMessage,
  ] = useState("");


  /* =======================================================
     MESSAGE TYPE
  ======================================================= */

  const [
    messageType,
    setMessageType,
  ] = useState("");


  /* =======================================================
     CLEAR MESSAGE
  ======================================================= */

  const clearMessage = () => {

    setMessage("");

    setMessageType("");

  };


  /* =======================================================
     ERROR
  ======================================================= */

  const showError = (text) => {

    setMessage(text);

    setMessageType("error");

  };


  /* =======================================================
     SUCCESS
  ======================================================= */

  const showSuccess = (text) => {

    setMessage(text);

    setMessageType("success");

  };


  /* =======================================================
     RESET FORM
  ======================================================= */

  const resetForm = () => {

    setSelectedQuestion("");

    setSecurityAnswer("");

    setOldPassword("");

    setNewPassword("");

    setConfirmPassword("");

    setShowOldPassword(false);

    setShowNewPassword(false);

    setShowConfirmPassword(false);

  };


  /* =========================================================
     CHECK SECURITY INFORMATION
  ========================================================= */

  const hasSecurityInformation = (data) => {

    if (!data) {

      return false;

    }


    const securityQuestion =
      String(
        data.securityQuestion || ""
      ).trim();


    const securityAnswer =
      String(
        data.securityAnswer || ""
      ).trim();


    return (
      securityQuestion !== "" &&
      securityAnswer !== ""
    );

  };


  /* =========================================================
     FIND USER BY USERNAME
  ========================================================= */

  const findUserByUsername = async () => {

    if (!username) {

      return null;

    }


    const usersRef =
      collection(
        db,
        "users"
      );


    const usersQuery =
      query(
        usersRef,
        where(
          "username",
          "==",
          username
        )
      );


    const snapshot =
      await getDocs(
        usersQuery
      );


    if (snapshot.empty) {

      return null;

    }


    const firstDocument =
      snapshot.docs[0];


    return {

      ref:
        firstDocument.ref,

      data:
        firstDocument.data(),

    };

  };


  /* =========================================================
     FIND SECURITY BY USERNAME
  ========================================================= */

  const findSecurityByUsername = async (
    fallbackUserData = null
  ) => {

    try {

      if (!username) {

        return null;

      }


      const securityRef =
        collection(
          db,
          "security"
        );


      const securityQuery =
        query(
          securityRef,
          where(
            "username",
            "==",
            username
          )
        );


      const snapshot =
        await getDocs(
          securityQuery
        );


      /* ===================================================
         SECURITY DOCUMENT FOUND
      =================================================== */

      if (!snapshot.empty) {

        const firstDocument =
          snapshot.docs[0];


        return {

          ref:
            firstDocument.ref,

          data:
            firstDocument.data(),

        };

      }


      /* ===================================================
         FALLBACK TO USERS DOCUMENT
      =================================================== */

      if (
        fallbackUserData &&
        (
          fallbackUserData.securityQuestion ||
          fallbackUserData.securityAnswer ||
          fallbackUserData.withdrawPassword
        )
      ) {

        return {

          ref:
            null,

          data:
            fallbackUserData,

        };

      }


      return null;

    } catch (error) {

      console.error(
        "Security lookup error:",
        error
      );

      return null;

    }

  };


  /* =========================================================
     LOAD ACCOUNT DATA
  ========================================================= */

  useEffect(() => {

    let mounted = true;


    const loadData = async () => {

      try {

        setLoading(true);

        clearMessage();


        /* ===============================================
           USERNAME CHECK
        =============================================== */

        if (!username) {

          if (mounted) {

            setMessage(
              "Username was not found in local storage."
            );

            setMessageType(
              "error"
            );

          }

          return;

        }


        /* ===============================================
           FIND USER
        =============================================== */

        const foundUser =
          await findUserByUsername();


        if (!foundUser) {

          if (mounted) {

            setUserData(null);

            setUserDocRef(null);

            setSecurityData(null);

            setSecurityDocRef(null);

            setMessage(
              `User "${username}" was not found in the users collection.`
            );

            setMessageType(
              "error"
            );

          }

          return;

        }


        /* ===============================================
           SAVE USER DATA
        =============================================== */

        if (mounted) {

          setUserData(
            foundUser.data
          );

          setUserDocRef(
            foundUser.ref
          );

        }


        /* ===============================================
           FIND SECURITY
        =============================================== */

        const foundSecurity =
          await findSecurityByUsername(
            foundUser.data
          );


        if (mounted) {

          if (foundSecurity) {

            setSecurityData(
              foundSecurity.data
            );

            setSecurityDocRef(
              foundSecurity.ref
            );

          } else {

            setSecurityData(
              null
            );

            setSecurityDocRef(
              null
            );

          }

        }

      } catch (error) {

        console.error(
          "Failed to load account data:",
          error
        );


        if (mounted) {

          setMessage(
            "Unable to load your account information."
          );

          setMessageType(
            "error"
          );

        }

      } finally {

        if (mounted) {

          setLoading(false);

        }

      }

    };


    loadData();


    return () => {

      mounted = false;

    };

  }, [
    username,
  ]);


  /* =======================================================
     OPEN LOGIN PASSWORD
  ======================================================= */

  const openLoginPassword = async () => {

    if (saving) {

      return;

    }


    clearMessage();


    if (!username) {

      showError(
        "Username was not found in local storage."
      );

      return;

    }


    try {

      let currentSecurityData =
        securityData;


      if (!currentSecurityData) {

        const foundSecurity =
          await findSecurityByUsername(
            userData
          );


        if (foundSecurity) {

          currentSecurityData =
            foundSecurity.data;


          setSecurityData(
            foundSecurity.data
          );


          setSecurityDocRef(
            foundSecurity.ref
          );

        }

      }


      if (
        !hasSecurityInformation(
          currentSecurityData
        )
      ) {

        navigate(
          "/settings"
        );

        return;

      }


      resetForm();


      setPasswordType(
        PASSWORD_TYPES.LOGIN
      );

    } catch (error) {

      console.error(
        "Checking security information failed:",
        error
      );


      showError(
        "Unable to check your security information. Please try again."
      );

    }

  };


  /* =========================================================
     OPEN WITHDRAWAL PASSWORD
  ========================================================= */

  const openWithdrawalPassword = async () => {

    if (saving) {

      return;

    }


    clearMessage();


    if (!username) {

      showError(
        "Username was not found in local storage."
      );

      return;

    }


    try {

      let currentSecurityData =
        securityData;


      if (!currentSecurityData) {

        const foundSecurity =
          await findSecurityByUsername(
            userData
          );


        if (foundSecurity) {

          currentSecurityData =
            foundSecurity.data;


          setSecurityData(
            foundSecurity.data
          );


          setSecurityDocRef(
            foundSecurity.ref
          );

        }

      }


      if (
        !hasSecurityInformation(
          currentSecurityData
        )
      ) {

        navigate(
          "/settings"
        );

        return;

      }


      resetForm();


      setPasswordType(
        PASSWORD_TYPES.WITHDRAW
      );

    } catch (error) {

      console.error(
        "Checking security information failed:",
        error
      );


      showError(
        "Unable to check your security information. Please try again."
      );

    }

  };


  /* =======================================================
     CLOSE FORM
  ======================================================= */

  const closeForm = () => {

    if (saving) {

      return;

    }


    resetForm();

    clearMessage();

    setPasswordType(null);

  };


  /* =======================================================
     VERIFY SECURITY QUESTION
  ======================================================= */

  const verifySecurityAnswer = () => {

    if (!securityData) {

      showError(
        "Security information is not available."
      );

      return false;

    }


    if (!selectedQuestion) {

      showError(
        "Please select your security question."
      );

      return false;

    }


    const savedQuestion =
      String(
        securityData.securityQuestion || ""
      ).trim();


    if (!savedQuestion) {

      showError(
        "Your security question has not been set. Please complete your security information in Settings."
      );

      return false;

    }


    if (
      selectedQuestion !==
      savedQuestion
    ) {

      showError(
        "The selected security question is incorrect."
      );

      return false;

    }


    const enteredAnswer =
      String(
        securityAnswer || ""
      )
        .trim()
        .toLowerCase();


    const savedAnswer =
      String(
        securityData.securityAnswer || ""
      )
        .trim()
        .toLowerCase();


    if (!enteredAnswer) {

      showError(
        "Please enter your security answer."
      );

      return false;

    }


    if (!savedAnswer) {

      showError(
        "Your security answer has not been set. Please complete your security information in Settings."
      );

      return false;

    }


    if (
      enteredAnswer !==
      savedAnswer
    ) {

      showError(
        "Incorrect security answer."
      );

      return false;

    }


    return true;

  };


  /* =======================================================
     VALIDATE OLD PASSWORD
  ======================================================= */

  const validateOldPassword = () => {

    if (!oldPassword) {

      showError(
        `Please enter your old ${
          passwordType === PASSWORD_TYPES.LOGIN
            ? "login"
            : "withdrawal"
        } password.`
      );

      return false;

    }


    return true;

  };


  /* =======================================================
     VALIDATE LOGIN PASSWORD
  ======================================================= */

  const validateLoginPassword = () => {

    if (!newPassword) {

      showError(
        "Please enter your new login password."
      );

      return false;

    }


    if (
      newPassword.length < 6
    ) {

      showError(
        "Login password must contain at least 6 characters."
      );

      return false;

    }


    if (
      newPassword !==
      confirmPassword
    ) {

      showError(
        "New password and confirmation password do not match."
      );

      return false;

    }


    if (
      newPassword ===
      oldPassword
    ) {

      showError(
        "New password must be different from your old password."
      );

      return false;

    }


    return true;

  };


  /* =======================================================
     VALIDATE WITHDRAWAL PASSWORD
  ======================================================= */

  const validateWithdrawalPassword = () => {

    if (!newPassword) {

      showError(
        "Please enter your new withdrawal password."
      );

      return false;

    }


    if (
      newPassword.length < 4
    ) {

      showError(
        "Withdrawal password must contain at least 4 characters."
      );

      return false;

    }


    if (
      newPassword
        .trim()
        .toLowerCase() ===
      username
        .trim()
        .toLowerCase()
    ) {

      showError(
        "Withdrawal password cannot be the same as your username."
      );

      return false;

    }


    if (
      newPassword !==
      confirmPassword
    ) {

      showError(
        "New password and confirmation password do not match."
      );

      return false;

    }


    if (
      newPassword ===
      oldPassword
    ) {

      showError(
        "New password must be different from your old password."
      );

      return false;

    }


    return true;

  };


  /* =======================================================
     CHANGE LOGIN PASSWORD
  ======================================================= */

  const changeLoginPassword = async () => {

    if (
      !validateLoginPassword()
    ) {

      return false;

    }


    try {

      const foundUser =
        await findUserByUsername();


      if (!foundUser) {

        showError(
          `User "${username}" was not found in the users collection.`
        );

        return false;

      }


      setUserDocRef(
        foundUser.ref
      );


      setUserData(
        foundUser.data
      );


      const savedPassword =
        String(
          foundUser.data.password || ""
        );


      if (!savedPassword) {

        showError(
          "Login password was not found in your user profile."
        );

        return false;

      }


      if (
        oldPassword !==
        savedPassword
      ) {

        showError(
          "Old login password is incorrect."
        );

        return false;

      }


      await setDoc(
        foundUser.ref,
        {

          password:
            newPassword,

          passwordUpdatedAt:
            serverTimestamp(),

        },
        {
          merge: true,
        }
      );


      setUserData(
        previous => ({

          ...(previous || {}),

          password:
            newPassword,

        })
      );


      showSuccess(
        "Login password changed successfully."
      );


      resetForm();


      return true;

    } catch (error) {

      console.error(
        "Change login password error:",
        error
      );


      showError(
        "Failed to change login password. Please try again."
      );


      return false;

    }

  };


  /* =======================================================
     CHANGE WITHDRAWAL PASSWORD
  ======================================================= */

  const changeWithdrawalPassword =
    async () => {

      if (
        !validateWithdrawalPassword()
      ) {

        return false;

      }


      try {

        const foundSecurity =
          await findSecurityByUsername(
            userData
          );


        if (!foundSecurity) {

          navigate(
            "/settings"
          );

          return false;

        }


        const latestData =
          foundSecurity.data;


        if (
          !hasSecurityInformation(
            latestData
          )
        ) {

          navigate(
            "/settings"
          );

          return false;

        }


        setSecurityData(
          latestData
        );


        setSecurityDocRef(
          foundSecurity.ref
        );


        const savedWithdrawalPassword =
          String(
            latestData.withdrawPassword || ""
          );


        if (
          !savedWithdrawalPassword
        ) {

          showError(
            "No withdrawal password has been set yet."
          );

          return false;

        }


        if (
          oldPassword !==
          savedWithdrawalPassword
        ) {

          showError(
            "Old withdrawal password is incorrect."
          );

          return false;

        }


        if (foundSecurity.ref) {

          await setDoc(
            foundSecurity.ref,
            {

              username,

              withdrawPassword:
                newPassword,

              updatedAt:
                serverTimestamp(),

            },
            {
              merge: true,
            }
          );

        } else {

          if (!userDocRef) {

            showError(
              "User document reference is not available."
            );

            return false;

          }


          await setDoc(
            userDocRef,
            {

              withdrawPassword:
                newPassword,

              updatedAt:
                serverTimestamp(),

            },
            {
              merge: true,
            }
          );

        }


        setSecurityData(
          previous => ({

            ...(previous || {}),

            withdrawPassword:
              newPassword,

          })
        );


        showSuccess(
          "Withdrawal password changed successfully."
        );


        resetForm();


        return true;

      } catch (error) {

        console.error(
          "Change withdrawal password error:",
          error
        );


        showError(
          "Failed to change withdrawal password. Please try again."
        );


        return false;

      }

    };


  /* =======================================================
     HANDLE CHANGE PASSWORD
  ======================================================= */

  const handleChangePassword =
    async () => {

      clearMessage();


      if (!username) {

        showError(
          "Username was not found in local storage."
        );

        return;

      }


      if (!userData) {

        showError(
          `User "${username}" was not found in the users collection.`
        );

        return;

      }


      if (
        !hasSecurityInformation(
          securityData
        )
      ) {

        navigate(
          "/settings"
        );

        return;

      }


      if (!securityData) {

        navigate(
          "/settings"
        );

        return;

      }


      if (
        !verifySecurityAnswer()
      ) {

        return;

      }


      if (
        !validateOldPassword()
      ) {

        return;

      }


      setSaving(true);


      try {

        if (
          passwordType ===
          PASSWORD_TYPES.LOGIN
        ) {

          await changeLoginPassword();

        } else if (
          passwordType ===
          PASSWORD_TYPES.WITHDRAW
        ) {

          await changeWithdrawalPassword();

        }

      } finally {

        setSaving(false);

      }

    };


  /* =======================================================
     BACK
  ======================================================= */

  const handleBack = () => {

    if (saving) {

      return;

    }


    if (passwordType) {

      closeForm();

      return;

    }


    navigate(-1);

  };


  /* =======================================================
     LOGOUT
  ======================================================= */

  const handleLogout = () => {

    if (saving) {

      return;

    }


    navigate(
      "/logout",
      {
        state: {
          from: "/settings/change-password",
        },
      }
    );

  };


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {

    return (

      <div className="profile-change-page">

        <div className="profile-change-loading">

          <FaSpinner
            className="profile-change-spinner"
          />

          <span>
            Loading account information...
          </span>

        </div>

      </div>

    );

  }


  /* =======================================================
     PAGE
  ======================================================= */

  return (

    <div className="profile-change-page">

      <main className="profile-change-content">


        {/* =================================================
            HEADER
        ================================================= */}

        <header className="profile-change-header">

          <button
            type="button"
            className="profile-change-back"
            onClick={handleBack}
            disabled={saving}
            aria-label="Go back"
          >

            <FaArrowLeft />

          </button>


          <div>

            <span className="profile-change-eyebrow">
              SECURITY
            </span>

            <h1>
              Change Password
            </h1>

            <p>
              Choose the password you want to change.
            </p>

          </div>

        </header>


        {/* =================================================
            ACCOUNT
        ================================================= */}

        {!passwordType && userData && (

          <div className="profile-change-security-ready">

            <FaCheckCircle />

            <div>

              <strong>
                Account: {username}
              </strong>

              <span>
                Choose Login Password or Withdrawal Password.
              </span>

            </div>

          </div>

        )}


        {/* =================================================
            MESSAGE
        ================================================= */}

        {message && !passwordType && (

          <div
            className={
              messageType === "success"
                ? "profile-change-message success"
                : "profile-change-message error"
            }
          >

            {messageType === "success" ? (

              <FaCheckCircle />

            ) : (

              <FaTimes />

            )}

            <span>
              {message}
            </span>

          </div>

        )}


        {/* =================================================
            PASSWORD SELECTION
        ================================================= */}

        {!passwordType && (

          <section className="profile-change-card">

            <div className="profile-change-card-heading">

              <FaKey />

              <div>

                <h2>
                  Choose Password
                </h2>

                <span>
                  Select the password you want to change.
                </span>

              </div>

            </div>


            <div className="profile-change-type-buttons">


              {/* ===========================================
                  LOGIN PASSWORD
              =========================================== */}

              <button
                type="button"
                className="profile-change-type"
                onClick={openLoginPassword}
                disabled={saving}
              >

                <div className="profile-change-type-icon">

                  <FaLock />

                </div>


                <div>

                  <strong>
                    Login Password
                  </strong>

                  <span>
                    Password used to enter your account.
                  </span>

                </div>

              </button>


              {/* ===========================================
                  WITHDRAWAL PASSWORD
              =========================================== */}

              <button
                type="button"
                className="profile-change-type"
                onClick={openWithdrawalPassword}
                disabled={saving}
              >

                <div className="profile-change-type-icon">

                  <FaShieldAlt />

                </div>


                <div>

                  <strong>
                    Withdrawal Password
                  </strong>

                  <span>
                    Password used when withdrawing money.
                  </span>

                </div>

              </button>


            </div>

          </section>

        )}


        {/* =================================================
            FORM
        ================================================= */}

        {passwordType && (

          <>

            {/* =============================================
                SELECTED PASSWORD
            ============================================= */}

            <section className="profile-change-card">

              <div className="profile-change-card-heading">

                {passwordType ===
                  PASSWORD_TYPES.LOGIN ? (

                  <FaLock />

                ) : (

                  <FaShieldAlt />

                )}


                <div>

                  <h2>

                    {passwordType ===
                      PASSWORD_TYPES.LOGIN
                        ? "Login Password"
                        : "Withdrawal Password"
                    }

                  </h2>


                  <span>

                    {passwordType ===
                      PASSWORD_TYPES.LOGIN
                        ? "Change the password used to log into your account."
                        : "Change the password used when withdrawing money."
                    }

                  </span>

                </div>

              </div>

            </section>


            {/* =============================================
                SECURITY FORM
            ============================================= */}

            <section className="profile-change-card">

              <div className="profile-change-card-heading">

                <FaShieldAlt />

                <div>

                  <h2>
                    Security Verification
                  </h2>

                  <span>
                    Verify your identity before changing your password.
                  </span>

                </div>

              </div>


              {/* =========================================
                  SECURITY QUESTION
              ========================================= */}

              <div className="profile-change-field">

                <label>
                  Security Question
                </label>


                <select
                  value={selectedQuestion}
                  onChange={(event) => {

                    setSelectedQuestion(
                      event.target.value
                    );

                    clearMessage();

                  }}
                  disabled={saving}
                >

                  <option value="">
                    Select your security question
                  </option>


                  {securityQuestions.map(
                    (question, index) => (

                      <option
                        key={index}
                        value={question}
                      >

                        {question}

                      </option>

                    )
                  )}

                </select>


                <small>
                  Select the same security question you used when setting up your profile.
                </small>

              </div>


              {/* =========================================
                  SECURITY ANSWER
              ========================================= */}

              <div className="profile-change-field">

                <label>
                  Security Answer
                </label>


                <input
                  type="text"
                  value={securityAnswer}
                  onChange={(event) => {

                    setSecurityAnswer(
                      event.target.value
                    );

                    clearMessage();

                  }}
                  placeholder="Enter your security answer"
                  disabled={saving}
                  autoComplete="off"
                />


                <small>
                  Your security answer is checked before the password change.
                </small>

              </div>


              {/* =========================================
                  OLD PASSWORD
              ========================================= */}

              <div className="profile-change-field">

                <label>

                  Old{" "}

                  {passwordType ===
                    PASSWORD_TYPES.LOGIN
                      ? "Login"
                      : "Withdrawal"
                  }{" "}

                  Password

                </label>


                <div className="profile-change-password-input">

                  <input
                    type={
                      showOldPassword
                        ? "text"
                        : "password"
                    }
                    value={oldPassword}
                    onChange={(event) => {

                      setOldPassword(
                        event.target.value
                      );

                      clearMessage();

                    }}
                    placeholder={
                      passwordType ===
                      PASSWORD_TYPES.LOGIN
                        ? "Enter your old login password"
                        : "Enter your old withdrawal password"
                    }
                    disabled={saving}
                    autoComplete="current-password"
                  />


                  <button
                    type="button"
                    onClick={() =>
                      setShowOldPassword(
                        value => !value
                      )
                    }
                    disabled={saving}
                    aria-label={
                      showOldPassword
                        ? "Hide old password"
                        : "Show old password"
                    }
                  >

                    {showOldPassword ? (

                      <FaEyeSlash />

                    ) : (

                      <FaEye />

                    )}

                  </button>

                </div>

              </div>


              {/* =========================================
                  NEW PASSWORD
              ========================================= */}

              <div className="profile-change-field">

                <label>

                  New{" "}

                  {passwordType ===
                    PASSWORD_TYPES.LOGIN
                      ? "Login"
                      : "Withdrawal"
                  }{" "}

                  Password

                </label>


                <div className="profile-change-password-input">

                  <input
                    type={
                      showNewPassword
                        ? "text"
                        : "password"
                    }
                    value={newPassword}
                    onChange={(event) => {

                      setNewPassword(
                        event.target.value
                      );

                      clearMessage();

                    }}
                    placeholder={
                      passwordType ===
                      PASSWORD_TYPES.LOGIN
                        ? "Enter your new login password"
                        : "Enter your new withdrawal password"
                    }
                    disabled={saving}
                    autoComplete="new-password"
                  />


                  <button
                    type="button"
                    onClick={() =>
                      setShowNewPassword(
                        value => !value
                      )
                    }
                    disabled={saving}
                    aria-label={
                      showNewPassword
                        ? "Hide new password"
                        : "Show new password"
                    }
                  >

                    {showNewPassword ? (

                      <FaEyeSlash />

                    ) : (

                      <FaEye />

                    )}

                  </button>

                </div>


                {passwordType ===
                  PASSWORD_TYPES.LOGIN && (

                  <small>
                    Login password must contain at least 6 characters.
                  </small>

                )}


                {passwordType ===
                  PASSWORD_TYPES.WITHDRAW && (

                  <small>
                    Withdrawal password must contain at least 4 characters and cannot be the same as your username.
                  </small>

                )}

              </div>


              {/* =========================================
                  CONFIRM PASSWORD
              ========================================= */}

              <div className="profile-change-field">

                <label>
                  Confirm New Password
                </label>


                <div className="profile-change-password-input">

                  <input
                    type={
                      showConfirmPassword
                        ? "text"
                        : "password"
                    }
                    value={confirmPassword}
                    onChange={(event) => {

                      setConfirmPassword(
                        event.target.value
                      );

                      clearMessage();

                    }}
                    placeholder="Confirm your new password"
                    disabled={saving}
                    autoComplete="new-password"
                  />


                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        value => !value
                      )
                    }
                    disabled={saving}
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                  >

                    {showConfirmPassword ? (

                      <FaEyeSlash />

                    ) : (

                      <FaEye />

                    )}

                  </button>

                </div>

              </div>


              {/* =========================================
                  MESSAGE
              ========================================= */}

              {message && (

                <div
                  className={
                    messageType === "success"
                      ? "profile-change-message success"
                      : "profile-change-message error"
                  }
                >

                  {messageType === "success" ? (

                    <FaCheckCircle />

                  ) : (

                    <FaTimes />

                  )}

                  <span>
                    {message}
                  </span>

                </div>

              )}


              {/* =========================================
                  ACTIONS
              ========================================= */}

              <div className="profile-change-actions">

                <button
                  type="button"
                  className="profile-change-cancel"
                  onClick={closeForm}
                  disabled={saving}
                >

                  <FaArrowLeft />

                  Back

                </button>


                <button
                  type="button"
                  className="profile-change-save"
                  onClick={handleChangePassword}
                  disabled={saving}
                >

                  {saving ? (

                    <>

                      <FaSpinner
                        className="profile-change-button-spinner"
                      />

                      Changing...

                    </>

                  ) : (

                    <>

                      <FaSave />

                      Change Password

                    </>

                  )}

                </button>

              </div>


            </section>


            {/* =============================================
                INFORMATION
            ============================================= */}

            <div className="profile-change-info">

              <FaLock />

              <div>

                <strong>
                  Password security
                </strong>

                <span>
                  Security question, security answer and old password are required before a new password can be set.
                </span>

              </div>

            </div>

          </>

        )}


        {/* =================================================
            LOGOUT
        ================================================= */}

        <section className="profile-change-logout-card">

          <div className="profile-change-logout-icon">
            <FaSignOutAlt />
          </div>

          <div className="profile-change-logout-content">

            <strong>
              Account Session
            </strong>

            <span>
              Sign out and securely clear this account session.
            </span>

          </div>

          <button
            type="button"
            className="profile-change-logout-button"
            onClick={handleLogout}
            disabled={saving}
          >
            <FaSignOutAlt />
            Log out
          </button>

        </section>


      </main>

    </div>

  );

}